import PropTypes from "prop-types";

// react-router-dom components
import { Link } from "react-router-dom";

// @mui material components
import Icon from "@mui/material/Icon";
import MuiLink from "@mui/material/Link";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";
import { useTranslation } from "react-i18next";

const buttonStyles = {
  display: "inline-flex",
  alignItems: "center",
  columnGap: 4,
  textDecoration: "none",
  cursor: "pointer",
  transition: "transform 150ms ease, opacity 150ms ease",
  "&:hover": {
    transform: "translateX(2px)",
    opacity: 0.9,
  },
};

function FilledInfoCard({
  variant,
  color,
  icon,
  title,
  description,
  action,
  isPlanCard,
  planExpireAt,
  noPlanMessage,
}) {
  const { t } = useTranslation();

  let planCtaKey = null;
  let iconColor = color;
  const isContained = variant === "contained";

  // 默认描述样式
  let descriptionColor = isContained ? "white" : color === "light" ? "text" : "white";
  let descriptionText =
    typeof description === "string" || typeof description === "number" ? description : "";

  // 如果是套餐状态卡片，则根据有效期动态调整颜色和文案
  if (isPlanCard) {
    const baseNoPlanText = noPlanMessage || t("userInfo.planInactive");

    if (!planExpireAt) {
      // 从未购买过或没有有效期
      descriptionText = baseNoPlanText;
      descriptionColor = "warning";
      planCtaKey = "open"; // 从未开通过：立即开通套餐
    } else {
      const now = new Date();
      const exp = new Date(planExpireAt);
      if (!Number.isNaN(exp.getTime())) {
        const diffMs = exp.getTime() - now.getTime();
        const diffDays = diffMs / (1000 * 60 * 60 * 24);
        const dateStr = planExpireAt.toString().slice(0, 10); // 简单截取日期部分

        if (exp < now) {
          // 已过期：红色 + 已过期标记
          descriptionText = t("userInfo.planExpireExpired", { date: dateStr });
          descriptionColor = "error";
          planCtaKey = "reopen"; // 已过期：重新开通套餐
        } else if (diffDays > 7) {
          // 有效期超过一周：绿色
          descriptionText = t("userInfo.planExpire", { date: dateStr });
          descriptionColor = "success";
          planCtaKey = "renew"; // 未过期：续费延长有效期
        } else {
          // 有效期不足一周：黄色
          descriptionText = t("userInfo.planExpire", { date: dateStr });
          descriptionColor = "warning";
          planCtaKey = "renew"; // 未过期：续费延长有效期
        }
      } else {
        // 解析失败时，保持可渲染的描述（避免 object 导致 prop type 警告）
        descriptionText =
          typeof description === "string" || typeof description === "number" ? description : "";
      }
    }
  }

  if (variant === "gradient" && color !== "light") {
    iconColor = "white";
  } else if (variant === "gradient" && color === "light") {
    iconColor = "dark";
  }

  return (
    <MKBox
      display={{ xs: "block", md: "flex" }}
      variant={variant}
      bgColor={isContained ? "rgba(17, 24, 39, 0.75)" : color}
      borderRadius="xl"
      pt={3.5}
      pb={3}
      px={3}
      sx={
        isContained
          ? {
              backgroundColor: "rgba(17, 24, 39, 0.75)", // increased opacity
              boxShadow: "0 18px 45px rgba(15, 23, 42, 0.75)", // stronger shadow so差异更大
            }
          : {}
      }
    >
      <MKTypography
        display="block"
        variant="h3"
        color={iconColor}
        textGradient={variant === "gradient"}
        mt={-0.625}
      >
        {typeof icon === "string" ? <Icon>{icon}</Icon> : icon}
      </MKTypography>
      <MKBox pt={{ xs: 3, md: 0 }} pl={{ xs: 0, md: 2 }} lineHeight={1}>
        <MKTypography
          display="block"
          variant="h6"
          color={isContained ? "white" : color === "light" ? "dark" : "white"}
          fontWeight="bold"
          mb={1}
          sx={{ fontSize: "1rem" }} // smaller title
        >
          {title}
        </MKTypography>
        <MKTypography
          display="block"
          variant="body2"
          color={descriptionColor}
          mb={2}
          sx={{ fontSize: "0.85rem", lineHeight: 1.3 }} // smaller + tighter layout
        >
          {descriptionText}
        </MKTypography>
        {action && action.type === "external" ? (
          <MKTypography
            component={MuiLink}
            href={action.route}
            target="_blank"
            rel="noreferrer"
            variant="body2"
            fontWeight="regular"
            color={isContained ? "info" : "white"}
            sx={buttonStyles}
          >
            {(isPlanCard && planCtaKey ? t(`userInfo.planCta.${planCtaKey}`) : action.label) || ""}{" "}
            <Icon sx={{ fontWeight: "bold" }}>{action.icon || "arrow_forward"}</Icon>
          </MKTypography>
        ) : null}
        {action && action.type === "internal" ? (
          <MKTypography
            component={Link}
            to={action.route}
            variant="body2"
            fontWeight="regular"
            color={isContained ? "info" : "white"}
            sx={buttonStyles}
          >
            {(isPlanCard && planCtaKey ? t(`userInfo.planCta.${planCtaKey}`) : action.label) || ""}{" "}
            <Icon sx={{ fontWeight: "bold" }}>{action.icon || "arrow_forward"}</Icon>
          </MKTypography>
        ) : null}
        {action && action.type === "scroll" ? (
          <MKTypography
            component="span"
            onClick={() => {
              if (typeof action.onClick === "function") {
                action.onClick();
              }
            }}
            variant="body2"
            fontWeight="regular"
            color={isContained ? "info" : "white"}
            sx={{ ...buttonStyles, cursor: "pointer" }}
          >
            {(isPlanCard && planCtaKey ? t(`userInfo.planCta.${planCtaKey}`) : action.label) || ""}{" "}
            <Icon sx={{ fontWeight: "bold" }}>{action.icon || "arrow_forward"}</Icon>
          </MKTypography>
        ) : null}
      </MKBox>
    </MKBox>
  );
}

// Setting default props for the FilledInfoCard
FilledInfoCard.defaultProps = {
  variant: "contained",
  color: "info",
  icon: "info",
  title: "",
  description: "",
  action: false,
  isPlanCard: false,
  planExpireAt: null,
  noPlanMessage: "",
};

// Typechecking props for the FilledInfoCard
FilledInfoCard.propTypes = {
  variant: PropTypes.oneOf(["contained", "gradient"]),
  color: PropTypes.oneOf([
    "primary",
    "secondary",
    "info",
    "success",
    "warning",
    "error",
    "light",
    "dark",
  ]),
  icon: PropTypes.node,
  title: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  description: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  action: PropTypes.oneOfType([
    PropTypes.bool,
    PropTypes.shape({
      type: PropTypes.oneOf(["external", "internal", "scroll"]).isRequired,
      route: PropTypes.string,
      label: PropTypes.string.isRequired,
      onClick: PropTypes.func,
      icon: PropTypes.string,
    }),
  ]),
  // 是否用于展示当前套餐状态（开启后才会启用颜色逻辑）
  isPlanCard: PropTypes.bool,
  // 当前套餐的有效期（日期字符串），用于计算颜色和“已过期”等状态
  planExpireAt: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
  // 从未购买过时显示的提示文案
  noPlanMessage: PropTypes.string,
};

export default FilledInfoCard;
